type EvictHandler<T> = (value: T) => void;

type LruCache<T> = {
  get: (key: string) => T | undefined;
  peek: (key: string) => T | undefined;
  set: (key: string, value: T) => void;
  delete: (key: string) => void;
};

export function useLruCache<T>(maxEntries: number, onEvict?: EvictHandler<T>): LruCache<T> {
  const store = new Map<string, T>();

  const touch = (key: string, value: T) => {
    // Re-insert to the end to mark most-recently-used.
    store.delete(key);
    store.set(key, value);
  };

  const evictIfNeeded = () => {
    while (store.size > maxEntries) {
      const oldest = store.entries().next().value as [string, T] | undefined;
      if (!oldest) break;
      const [key, value] = oldest;
      store.delete(key);
      if (onEvict) {
        onEvict(value);
      }
    }
  };

  return {
    get(key: string) {
      const value = store.get(key);
      if (value !== undefined) {
        touch(key, value);
      }
      return value;
    },
    peek(key: string) {
      return store.get(key);
    },
    set(key: string, value: T) {
      touch(key, value);
      evictIfNeeded();
    },
    delete(key: string) {
      store.delete(key);
    },
  };
}
